

/**
 * @module request
 * @description
 * 高级 HTTP 请求库，基于 axios 实现，提供：
 * • 多域名智能负载均衡与自适应路由
 * • 预算型重试机制（总耗时上限控制）
 * • 熔断器模式故障隔离
 * • EWMA 延迟估计与 /ping 主动探测
 * • 完整的错误分类与差异化策略（429/502/503/超时/网络等）
 * • 只读接口容错与状态持久化
 * 
 * @example
 * // 1. 初始化：设置域名列表与只读接口
 * import request from '@/common/js/request';
 * 
 * // 配置域名（必装）
 * request.domain.updateList(['https://api1.example.com', 'https://api2.example.com', 'https://api3.example.com']);
 * 
 * // 配置只读接口（可选）
 * request.domain.updateReadOnlyApis(['/user/profile', '/product/list', '/config/fetch']);
 * 
 * // 2. 监听广播事件
 * import EventBus from '@/common/js/event-bus'; // 或您的事件总线实现
 * 
 * // 请求错误事件
 * EventBus.on('request:error', ({ message, code, config }) => {
 *   console.error('请求失败:', message, '状态码:', code);
 *   // message: 人性化错误消息（国际化已处理）
 *   // code: HTTP 状态码或错误类型（429/502/503/TIMEOUT等）
 *   // config: 原始请求配置
 * });
 * 
 * // 用户未登录事件（H5 特有）
 * EventBus.on('triggerLogin', ({ show }) => {
 *   router.push('/personal_login');
 * });
 * 
 * // Token 更新事件（H5 特有）
 * EventBus.on('accessTokenChange', (newToken) => {
 *   localStorage.setItem('qx2token', newToken);
 * });
 * 
 * // 用户已登录事件（H5 特有）
 * EventBus.on('userLogined', (userData) => {
 *   store.commit('setUserInfo', userData);
 * });
 * 
 * // 3. 发起请求
 * try {
 *   const response = await request.post('/user/login', {
 *     username: 'user',
 *     password: 'pass'
 *   }, {
 *     timeout: 30000,        // 整个请求+重试的总预算（ms）
 *     silent: false,         // 是否不触发错误提示（默认展示）
 *     serv: 'custom'         // 'custom' 时不加 /api/ 前缀
 *   });
 *   
 *   console.log(response.data);
 * } catch (err) {
 *   console.error('最终失败:', err);
 * }
 * 
 * // 4. 调试与监控
 * 
 * // 查看域名健康报告
 * console.table(request.domain.getHealthReport());
 * 
 * // 锁定到单个域名（强制使用该域名，跳过智能选路）
 * request.domain.lock('https://api1.example.com');
 * 
 * // 解除锁定，恢复自动选路
 * request.domain.unlock();
 * 
 * // 获取当前选中的域名
 * console.log(request.domain.getCurrent());
 * 
 * // 模拟网络错误便于测试各重试策略
 * request.domain.simulate('502');      // 将浏览器 Block 映射为 502
 * // 之后发起请求将触发 502 重试逻辑
 * request.domain.simulate(null);       // 关闭模拟
 * 
 * // 重置所有统计数据
 * request.domain.resetStats();
 * 
 * // 自测：验证核心逻辑
 * const { passed, failed, results } = await request.domain.runSelfTest();
 * console.log(`自测: ${passed} 通过, ${failed} 失败`, results);
 * 
 * @typedef {Object} RequestConfig
 * @property {string} [url] - 接口路径（无需包含 /api/ 前缀）
 * @property {'get'|'post'|'put'|'delete'|'patch'} [method='get'] - HTTP 方法
 * @property {*} [data] - 请求体（POST/PUT）
 * @property {*} [params] - 查询参数（GET）
 * @property {number} [timeout=30000] - 请求总预算（ms），包含重试
 * @property {boolean} [silent=false] - true 时不触发 request:error 事件
 * @property {boolean} [notips=false] - 向后兼容 silent 的旧名称
 * @property {string} [serv='default'] - 'custom' 时不加 /api/ 前缀
 * @property {Object} [headers={}] - 自定义请求头
 * @property {string} [headers.arrayFormat='indices'] - 数组参数格式
 * 
 * @typedef {Object} HealthReport
 * @property {string} domain - 域名
 * @property {number} score - 综合评分（0-100），越高越优先
 * @property {boolean} circuitOpen - 是否处于熔断状态
 * @property {string|null} circuitUntil - 熔断截止时间（ISO 8601）
 * @property {number} failCount - 累计失败次数
 * @property {string|null} lastFailTime - 最后失败时间（ISO 8601）
 * @property {number} ewmaRtt - 业务请求 RTT 的 EWMA 估计值（ms），-1 为无数据
 * @property {number} pingRtt - /ping 探测耗时（ms），-1 为无数据
 * @property {number} pingCost - /ping 返回的成本值，-1 为无数据
 * 
 * @event request:error
 * @description 请求最终失败时触发（所有重试策略均已耗尽）
 * @param {Object} payload
 * @param {string} payload.message - 人性化错误消息
 *   - "请求过于频繁，请稍后再试" （429）
 *   - "服务繁忙，请稍后再试" （503）
 *   - "网关错误，请稍后再试" （502）
 *   - "请求超时，请检查网络" （超时）
 *   - "网络连接失败" （网络错误）
 *   - "网络不稳定，请稍后重试" （所有域名均失败/预算耗尽）
 * @param {number|string} payload.code - 错误代码（HTTP 状态码或 'TIMEOUT'/'NETWORK_ERROR' 等）
 * @param {RequestConfig} payload.config - 原始请求配置
 * 
 * @event triggerLogin
 * @description 服务端返回"用户未登录"时触发（H5 特有）
 * @param {Object} payload
 * @param {boolean} payload.show - 是否应显示登录界面
 * 
 * @event accessTokenChange
 * @description 通过 /UserLogin 或 /UserInfo 获得新 Token 时触发（H5 特有）
 * @param {string} newToken - 新的访问令牌
 * 
 * @event userLogined
 * @description 用户成功登录后触发（H5 特有）
 * @param {Object} userData
 * @param {string} userData.id - 用户 ID
 * @param {string} userData.accessToken - 访问令牌
 * @param {*} [userData.*] - 其他用户字段
 * 
 * @note
 * 【核心设计】
 * • 预算型重试：timeout 参数代表整个请求+所有重试的总耗时上限，确保不会无限卡顿
 * • 路由决策：基于"失败计数、熔断状态、EWMA 延迟、ping 成本"的加权综合评分
 * • 差异化策略：
 *   - 429（限流）：立即上抛，不重试（客户端问题）
 *   - 502（网关故障）：立即切域或全域稍后重轮询
 *   - 503（过载）：同域快速重试 1 次，后指数退避+切域
 *   - 超时：判断"全域超时"或"单域超时"后不同处理
 *   - 网络错误：立即切域
 * • 熔断机制：失败 ≥2 次自动熔断 60s（收到 /ping 成功或域名再次成功时解除）
 * • 持久化：域名状态自动保存至 localStorage，页面刷新后恢复
 * • 只读接口容错：GET 接口首次失败可切域重试一次（具有幂等性保障）
 * 
 * @note
 * 【初始化清单】
 * 1. 在应用启动时调用 request.domain.updateList(domains)
 * 2. 可选：调用 request.domain.updateReadOnlyApis(readOnlyPaths) 配置幂等接口
 * 3. 在 Vue 应用中注册事件总线：request.mitter = eventBus（或自动从 app.config.globalProperties.mitter 读取）
 * 4. 在 Vue Router 中注册：request.router = router（用于在未登录时导航）
 * 5. 可选：在 Vue 中注册：request.mitter = emitter（内部使用，通常自动从 app.config.globalProperties.mitter 读取）
 * 
 * @note
 * 【监听事件清单】
 * • request:error → 展示 Toast/Notification（每次请求最终失败时触发 1 次）
 * • triggerLogin → 导航至登录页（用户会话过期）
 * • accessTokenChange → 更新本地 Token（自动 Token 刷新）
 * • userLogined → 更新用户信息（登录/个人资料接口成功）
 * 
 * @note
 * 【常见场景】
 * 场景 1：单个域名故障
 *   → 立即切换到次优域名，继续发送请求，用户无感
 * 
 * 场景 2：多个域名都故障（如 DDoS/机房故障）
 *   → 触发显示"网络不稳定，请稍后重试"，让用户手动重试
 * 
 * 场景 3：网络抖动导致超时
 *   → 自动等待 1.5s 后重试，若全域同时超时则判定为客户端问题
 * 
 * 场景 4：服务过载（503）
 *   → 先同域快速建立，再指数退避（2s → 4s → 8s...）后切域
 * 
 * 场景 5：用户被限流（429）
 *   → 立即上抛错误，提示用户"请求过于频繁"，不再重试
 */
import axios from 'axios';
import qs from 'qs';
import moment from 'moment';
import { getApiLang } from '@/utils/lang';

import { newGuid, queryString } from './base-utils';

import READ_ONLY_APIS from '@/data/readonly_apis.json';
import { recordApiRequestCount, recordApiRequestErrorCount, recordApiRequestDuration, recordApiTranDuration } from './otel-config';

// 配置区分（H5/PC）
const isH5 = false;
const DEFAULT_SOURCE = isH5 ? '3' : '5';
const DEVICE_ID_KEY = isH5 ? 'qx2DeviceID' : 'qx2DeviceID';
const DEVICE_ID_DEFAULT = isH5 ? newGuid() : 'spt-web';
const TOKEN_KEY = isH5 ? 'qx2token' : 'token';
const LOGIN_PATH = isH5 ? '/personal_login' : '/signIn';

// 定义错误类型常量（便于维护）
const ERROR_TYPE = {
  TIMEOUT: 'TIMEOUT',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN_ERR: 'UNKNOWN_ERR',
  NO_SERV_DURATION: 'NO_SERV_DURATION'
};

// ─────────────────────────────────────────────────────────────────────────────
// 重试策略常量
// ─────────────────────────────────────────────────────────────────────────────
// 默认总预算（ms）。调用方传入的 timeout 即代表整个请求+重试过程的上限。
const DEFAULT_BUDGET_MS = 30000;
// 熔断冷却时长：域名最近失败后，在此时间窗口内不作为首选，但仍可作降级兜底
const CIRCUIT_COOLDOWN_MS = 60 * 1000; // 60s
// /ping 探测间隔（ms）
const PING_INTERVAL_MS = 60 * 1000; // 60s
// /ping 单次超时（ms）
const PING_TIMEOUT_MS = 5000;
// EWMA 平滑因子（α）
const EWMA_ALPHA = 0.3;

// ─────────────────────────────────────────────────────────────────────────────
// 域名健康状态模型
// 每个域名的 stats 结构：
//   failCount       {number}  累计失败次数（成功后正向衰减）
//   lastFailTime    {number}  最近一次失败的时间戳（ms），0 表示从未失败
//   circuitUntil    {number}  熔断截止时间戳，到期前不作首选（0 = 未熔断）
//   ewmaRtt         {number}  业务请求 RTT 的 EWMA 估计值（ms），-1 = 无数据
//   pingRtt         {number}  /ping 探测 RTT（ms），-1 = 无数据
//   pingCost        {number}  /ping 返回的纯数字成本（越大成本越高），-1 = 无数据
// ─────────────────────────────────────────────────────────────────────────────
const domainConfig = {
  lockedDomain: localStorage.getItem('lockedDomain') || '',
  domainList: [],
  domainStats: { _lastSaved: 0 },
  readOnlyApis: READ_ONLY_APIS,
  // 调试模拟：将浏览器 Block（Network Error）映射为指定错误类型
  // 可选值: '429' | '502' | '503' | 'timeout' | null(关闭)
  _debugSimulation: null,

  get isLocked() {
    return !!this.lockedDomain;
  },

  // 初始化：从 localStorage 恢复状态，补全缺失字段
  init() {
    try {
      const raw = localStorage.getItem('domainStats');
      const saved = raw ? JSON.parse(raw) : null;
      if (saved && typeof saved === 'object') {
        Object.assign(this.domainStats, saved);
      }
    } catch (e) {
      console.error('[request] 读取域名状态失败，使用默认值', e);
    }
    this._ensureDomainStats(this.domainList);
  },

  // 确保列表内每个域名都有完整 stats 结构
  _ensureDomainStats(list) {
    list.forEach(domain => {
      if (!this.domainStats[domain]) {
        this.domainStats[domain] = this._defaultStats();
      } else {
        // 补充新增字段（向后兼容旧持久化数据）
        const s = this.domainStats[domain];
        if (s.ewmaRtt === undefined) s.ewmaRtt = -1;
        if (s.pingRtt === undefined) s.pingRtt = -1;
        if (s.pingCost === undefined) s.pingCost = -1;
        if (s.circuitUntil === undefined) s.circuitUntil = 0;
      }
    });
  },

  _defaultStats() {
    return { failCount: 0, lastFailTime: 0, circuitUntil: 0, ewmaRtt: -1, pingRtt: -1, pingCost: -1 };
  },

  // 立即持久化（重要状态变化时直接写入，不做防抖）
  _saveDomainStats() {
    try {
      this.domainStats._lastSaved = Date.now();
      localStorage.setItem('domainStats', JSON.stringify(this.domainStats));
    } catch (e) {
      console.warn('[request] 持久化域名状态失败', e);
    }
  },

  // 记录失败：累加失败次数，更新失败时间，按连续失败次数决定是否熔断
  recordFail(domain, { immediate = false } = {}) {
    const key = this.removeProtocol(domain);
    const s = this.domainStats[key];
    if (!s) return;
    s.failCount += 1;
    s.lastFailTime = Date.now();
    // 连续失败 ≥ 2 次时触发短暂熔断，避免同预算内反复命中同一死域名
    if (s.failCount >= 2 || immediate) {
      s.circuitUntil = Date.now() + CIRCUIT_COOLDOWN_MS;
    }
    this._saveDomainStats();
  },

  // 记录成功：EWMA 更新 RTT，失败计数正向衰减，清除熔断
  recordSuccess(domain, rttMs) {
    const key = this.removeProtocol(domain);
    const s = this.domainStats[key];
    if (!s) return;
    if (s.failCount > 0) s.failCount = Math.max(0, s.failCount - 1);
    s.circuitUntil = 0; // 成功后解除熔断
    s.ewmaRtt = s.ewmaRtt < 0 ? rttMs : s.ewmaRtt * (1 - EWMA_ALPHA) + rttMs * EWMA_ALPHA;
    this._saveDomainStats();
  },

  // 更新 /ping 探测结果
  recordPing(domain, rttMs, cost) {
    const key = this.removeProtocol(domain);
    const s = this.domainStats[key];
    if (!s) return;
    s.pingRtt = rttMs;
    s.pingCost = cost;
    // ping 成功也视为可达信号，轻微衰减失败计数
    if (s.failCount > 0) s.failCount = Math.max(0, s.failCount - 1);
    this._saveDomainStats();
  },

  // 标记域名单次超时（不立即熔断，但增加失败惩罚，供下次选路判断）
  recordTimeout(domain) {
    const key = this.removeProtocol(domain);
    const s = this.domainStats[key];
    if (!s) return;
    s.failCount += 1;
    s.lastFailTime = Date.now();
    // 超时2次及以上才熔断，避免偶发网络抖动导致过激熔断
    if (s.failCount >= 2) {
      s.circuitUntil = Date.now() + CIRCUIT_COOLDOWN_MS;
    }
    this._saveDomainStats();
  },

  // 判断域名是否处于熔断期
  isCircuitOpen(domain) {
    const key = this.removeProtocol(domain);
    const s = this.domainStats[key];
    if (!s) return false;
    return s.circuitUntil > Date.now();
  },

  // ─── 综合评分（值越高越优先）────────────────────────────────────────────
  // 各项满分 100，权重：
  //   失败惩罚   35%（failCount 相对最大值线性衰减）
  //   熔断惩罚   30%（熔断中为 0，未熔断为 30 分）
  //   EWMA RTT   20%（业务请求延迟，越低越好）
  //   ping 综合  15%（pingRtt 10% + pingCost 5%）
  calculateScore(domain) {
    const key = this.removeProtocol(domain);
    const s = this.domainStats[key];
    if (!s) return 0;

    // 熔断中直接给极低分（但不完全为0，以备全部域名熔断时降级兜底）
    if (this.isCircuitOpen(key)) return 1;

    const now = Date.now();

    // 1. 失败惩罚（相对其他域名的最大失败次数归一化）
    const allFails = this.domainList.map(d => (this.domainStats[d] || {}).failCount || 0);
    const maxFail = Math.max(...allFails, 1);
    const failFactor = Math.max(0, 1 - s.failCount / maxFail);
    const failScore = failFactor * 35;

    // 2. 熔断惩罚（0 = 未熔断得满分，熔断中已在最上方截断）
    const circuitScore = 30;

    // 3. 最近失败时间衰减（10分钟内线性衰减，超过后恢复，持续记账）
    let recentFailPenalty = 0;
    if (s.lastFailTime > 0) {
      const elapsed = now - s.lastFailTime;
      const decay = 10 * 60 * 1000;
      if (elapsed < decay) {
        recentFailPenalty = (1 - elapsed / decay) * 10; // 最大额外扣10分
      }
    }

    // 4. EWMA RTT 评分（0ms=100分，5000ms=0分，线性）
    const maxRtt = 5000;
    let rttScore;
    if (s.ewmaRtt < 0) {
      rttScore = 50; // 无数据时给中等分
    } else {
      rttScore = Math.max(0, 1 - Math.min(s.ewmaRtt, maxRtt) / maxRtt) * 100;
    }
    const rttWeight = rttScore * 0.2;

    // 5. ping 综合评分
    let pingRttScore = 50, pingCostScore = 50;
    if (s.pingRtt >= 0) {
      pingRttScore = Math.max(0, 1 - Math.min(s.pingRtt, maxRtt) / maxRtt) * 100;
    }
    if (s.pingCost >= 0) {
      // cost 越大越差，参考上限 100
      pingCostScore = Math.max(0, 1 - Math.min(s.pingCost, 100) / 100) * 100;
    }
    const pingWeight = (pingRttScore * 0.1 + pingCostScore * 0.05);

    const total = failScore + circuitScore - recentFailPenalty + rttWeight + pingWeight;
    console.debug('[request] 域名评分', key, { failScore, circuitScore, recentFailPenalty, rttWeight, pingWeight, total });
    return Math.max(0, total);
  },

  // 获取最优域名（带协议补全）
  // excludeDomains: string | string[] — 本轮预算内已尝试过的域名，不再命中
  getBestDomain(excludeDomains) {
    if (this.isLocked) {
      return this.completeProtocol(this.lockedDomain);
    }
    const excluded = excludeDomains
      ? (Array.isArray(excludeDomains) ? excludeDomains : [excludeDomains]).map(d => this.removeProtocol(d))
      : [];

    let candidates = this.domainList.filter(d => !excluded.includes(d));

    // 优先从未熔断的域名中选
    const healthy = candidates.filter(d => !this.isCircuitOpen(d));
    const pool = healthy.length > 0 ? healthy : candidates; // 全熔断时降级使用全部

    if (pool.length === 0) return '';

    const best = pool.reduce((a, b) => this.calculateScore(a) >= this.calculateScore(b) ? a : b);
    return this.completeProtocol(best);
  },

  // 检查是否所有域名都有近期超时记录（用于区分"单域故障"与"客户端网络问题"）
  isAllDomainsUnhealthy() {
    if (this.domainList.length === 0) return false;
    return this.domainList.every(d => this.isCircuitOpen(d) || (this.domainStats[d] || {}).failCount >= 1);
  },

  lockDomain(domain) {
    const key = this.removeProtocol(domain);
    this.lockedDomain = key;
    localStorage.setItem('lockedDomain', key);
  },

  unlockDomain() {
    this.lockedDomain = '';
    localStorage.removeItem('lockedDomain');
  },

  updateDomainList(newDomains) {
    if (!Array.isArray(newDomains) || newDomains.length === 0) {
      console.warn('[request] 无效的域名列表，更新失败');
      return false;
    }
    newDomains = newDomains.concat(this.domainList || []);
    const unique = [...new Set(newDomains.map(d => this.removeProtocol(d)))];
    this.domainList = unique;
    this._ensureDomainStats(unique);
    this._saveDomainStats();
    console.log('[request] 域名列表已更新:', unique);
    return true;
  },

  updateReadOnlyApis(apis) {
    if (Array.isArray(apis) && apis.length > 0) {
      const newApis = apis.map(a => a.toLocaleLowerCase());
      this.readOnlyApis = [...new Set([...this.readOnlyApis, ...newApis])];
      return true;
    }
    console.warn('[request] 无效的只读接口列表，更新失败');
    return false;
  },

  isReadOnlyApi(url) {
    if (!url) return false;
    const lower = url.toLocaleLowerCase();
    return this.readOnlyApis.some(a => lower === a);
  },

  removeProtocol(domain) {
    if (!domain) return '';
    return domain.replace(/^https?:\/\//, '').replace(/\/?$/, '');
  },

  completeProtocol(domain) {
    if (!domain) return '';
    if (/^https?:\/\//.test(domain)) return domain;
    const proto = window.location.protocol;
    return `${proto}//${domain}/`;
  }
};

// 初始化域名配置
domainConfig.init();

// ─────────────────────────────────────────────────────────────────────────────
// /ping 周期探测调度器
// ─────────────────────────────────────────────────────────────────────────────
const pingScheduler = {
  _timer: null,
  _running: false,

  start() {
    if (this._timer) return;
    // 启动后立即探一次，再按间隔周期执行
    this._probe();
    this._timer = setInterval(() => this._probe(), PING_INTERVAL_MS);
  },

  stop() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  },

  async _probe() {
    if (this._running || domainConfig.isLocked) return;
    if (domainConfig.domainList.length === 0) return;
    this._running = true;
    try {
      await Promise.allSettled(
        domainConfig.domainList.map(domain => this._pingOne(domain))
      );
    } finally {
      this._running = false;
    }
  },

  async _pingOne(domain) {
    const url = domainConfig.completeProtocol(domain) + 'ping';
    const t0 = Date.now();
    try {
      const resp = await fetch(url, {
        method: 'GET',
        signal: AbortSignal.timeout ? AbortSignal.timeout(PING_TIMEOUT_MS) : undefined,
        cache: 'no-store'
      });
      const rtt = Date.now() - t0;
      if (resp.ok) {
        const text = (await resp.text()).trim();
        const cost = parseFloat(text);
        domainConfig.recordPing(domain, rtt, isNaN(cost) ? -1 : cost);
        console.debug('[request][ping]', domain, rtt + 'ms', 'cost=' + cost);
      } else {
        domainConfig.recordFail(domain);
      }
    } catch (_) {
      // ping 失败不上报业务错误，只更新状态
      domainConfig.recordFail(domain);
    }
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 创建 axios 实例
// ─────────────────────────────────────────────────────────────────────────────
const instance = axios.create({
  timeout: DEFAULT_BUDGET_MS,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'Cache-Control': 'no-cache'
  }
});

// ─────────────────────────────────────────────────────────────────────────────
// 工具：重试等待
// ─────────────────────────────────────────────────────────────────────────────
function _sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// 计算本次尝试可用的单次超时（总预算 - 已耗时，保底 1000ms）
function _remainingTimeout(deadline) {
  return Math.max(1000, deadline - Date.now());
}

// ─────────────────────────────────────────────────────────────────────────────
// 核心：预算型重试执行器
//
// 设计要点：
//   - config.__canonicalPath：首次请求时固化，重试始终基于此路径重建 URL，
//     彻底规避"反处理 URL"的脆弱字符串操作。
//   - config.__budget：总预算 deadline（ms 时间戳），由首次请求设置。
//   - config.__tried：本轮预算内已尝试过的域名数组，防止同预算内回跳死域名。
//   - 分层策略：按状态码和错误类型执行不同退避/切域逻辑。
// ─────────────────────────────────────────────────────────────────────────────
async function executeWithBudget(originalConfig) {
  // ── 初始化预算上下文 ──────────────────────────────────────────────────────
  const budgetMs = originalConfig.timeout > 0 ? originalConfig.timeout : DEFAULT_BUDGET_MS;
  const deadline = Date.now() + budgetMs;

  // 在拦截器之前自行固化规范路径和只读判定（拦截器运行在 instance() 内部，此处更早）
  if (!originalConfig.__canonicalPath) {
    originalConfig.__canonicalPath = (originalConfig.url || '').replace(/^\//, '');
  }
  if (originalConfig.__isReadonly === undefined) {
    originalConfig.__isReadonly = domainConfig.isReadOnlyApi(originalConfig.__canonicalPath);
  }
  const canonicalPath = originalConfig.__canonicalPath;
  const isReadonly = originalConfig.__isReadonly;

  // 本轮预算内已尝试过的域名（raw key, 不含协议）
  const tried = [];

  // ── 选路：找到初始域名 ────────────────────────────────────────────────────
  let currentDomain = domainConfig.getBestDomain(tried);

  let attempt = 0;
  // 503 专用：同域快速重试已做过的次数，首次503先同域再切域
  let sameDomaln503Count = 0;

  while (Date.now() <= deadline) {
    attempt++;

    if (!currentDomain) {
      // 全部域名都试过，且无可用域名
      throw new Error('[request] 所有域名均不可用');
    }

    if (Date.now() >= deadline) {
      throw new Error('[request] 请求总预算已耗尽');
    }

    // ── 构建本次尝试的 config ─────────────────────────────────────────────
    const singleTimeout = _remainingTimeout(deadline);
    const attemptConfig = {
      ...originalConfig,
      baseURL: currentDomain,
      url: canonicalPath,           // 始终使用规范路径，不依赖 URL 反推
      timeout: singleTimeout,
      __isRetrying: attempt > 1,
      __budget: deadline,
      __tried: tried,
      __attempt: attempt
    };

    // 请求拦截器会重新拼接 _host + url，需要重置 _url/_host 避免双重拼接
    delete attemptConfig._url;
    delete attemptConfig._host;

    const domainKey = domainConfig.removeProtocol(currentDomain);
    tried.push(domainKey);

    try {
      const response = await instance(attemptConfig);
      return response; // 成功直接返回
    } catch (err) {
      let status = err.response?.status;
      let isTimeout = err.code === 'ECONNABORTED' || (err.message || '').toLowerCase().includes('timeout');
      let isNetErr = !err.response && (err.message || '').includes('Network Error');

      // ── 调试模拟：浏览器 Block → 映射为指定错误类型 ──────────────────
      if (isNetErr && domainConfig._debugSimulation) {
        const sim = String(domainConfig._debugSimulation);
        console.warn(`[request][debug] 网络错误已模拟为: ${sim}`);
        if (sim === '429') {
          err.response = { status: 429, data: {}, headers: {} };
          status = 429; isTimeout = false; isNetErr = false;
        } else if (sim === '502') {
          err.response = { status: 502, data: {}, headers: {} };
          status = 502; isTimeout = false; isNetErr = false;
        } else if (sim === '503') {
          err.response = { status: 503, data: {}, headers: {} };
          status = 503; isTimeout = false; isNetErr = false;
        } else if (sim === 'timeout') {
          err.code = 'ECONNABORTED';
          err.message = 'timeout of ' + singleTimeout + 'ms exceeded (simulated)';
          isTimeout = true; isNetErr = false;
        }
      }

      // ── 429：客户端被封锁，不重试，直接上抛 ────────────────────────────
      if (status === 429) {
        throw err;
      }

      // ── 502：LB 层故障，立即切域 ─────────────────────────────────────────
      if (status === 502) {
        domainConfig.recordFail(currentDomain, { immediate: true });
        const nextDomain = domainConfig.getBestDomain(tried);
        if (!nextDomain) {
          // 全域 502：等1.5s后重新尝试第一个域名（排除已连续失败的）
          if (Date.now() + 1500 < deadline) {
            await _sleep(1500);
            tried.length = 0; // 重置已尝试列表，重新轮询
            currentDomain = domainConfig.getBestDomain([]);
          } else {
            throw err;
          }
        } else {
          console.log('[request] 502 切换域名:', nextDomain);
          currentDomain = nextDomain;
        }
        continue;
      }

      // ── 503：实例过载，先同域一次，再指数退避+切域 ──────────────────────
      if (status === 503) {
        if (sameDomaln503Count === 0) {
          // 首次 503：快速同域再试一次（LB 可能调度到空闲实例）
          sameDomaln503Count++;
          tried.pop(); // 允许再次使用同一域名
          console.log('[request] 503 同域快速重试...');
          continue;
        }
        // 后续 503：指数退避后切域
        domainConfig.recordFail(currentDomain);
        const backoff = Math.min(2000 * (2 ** (sameDomaln503Count - 1)), 60000);
        sameDomaln503Count++;
        console.log('[request] 503 指数退避', backoff + 'ms 后切域...');
        if (Date.now() + backoff >= deadline) {
          throw err;
        }
        await _sleep(backoff);
        const nextDomain = domainConfig.getBestDomain(tried);
        currentDomain = nextDomain || domainConfig.getBestDomain([]); // 全熔断时降级
        continue;
      }

      // ── 超时 ──────────────────────────────────────────────────────────────
      if (isTimeout) {
        if (domainConfig.isAllDomainsUnhealthy()) {
          // 全域均有问题 → 判断为客户端网络问题，短等待后用最优域重试
          const wait = 1500;
          if (Date.now() + wait >= deadline) throw err;
          console.log('[request] 全域超时（客户端网络问题）, 等待后重试...');
          await _sleep(wait);
          // 不切域，继续用当前最优
          tried.pop();
          continue;
        } else {
          // 单域超时 → 高惩罚后立即切域
          domainConfig.recordTimeout(currentDomain);
          const nextDomain = domainConfig.getBestDomain(tried);
          if (!nextDomain) throw err;
          console.log('[request] 超时切换域名:', nextDomain);
          currentDomain = nextDomain;
          continue;
        }
      }

      // ── 网络错误（非超时）→ 立即切域 ────────────────────────────────────
      if (isNetErr) {
        domainConfig.recordFail(currentDomain, { immediate: true });
        const nextDomain = domainConfig.getBestDomain(tried);
        if (!nextDomain) throw err;
        console.log('[request] 网络错误切换域名:', nextDomain);
        currentDomain = nextDomain;
        continue;
      }

      // ── 只读接口其他错误：允许一次切域重试 ──────────────────────────────
      if (isReadonly && attempt === 1) {
        domainConfig.recordFail(currentDomain);
        const nextDomain = domainConfig.getBestDomain(tried);
        if (nextDomain && Date.now() < deadline) {
          currentDomain = nextDomain;
          continue;
        }
      }

      // ── 其他错误（4xx 业务错误等）：直接上抛 ────────────────────────────
      throw err;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 请求拦截器
// ─────────────────────────────────────────────────────────────────────────────
instance.interceptors.request.use(async config => {  
  config.requestSTime = performance.now();

  // ── Phase F: 规范化路径（仅首次请求固化，重试跳过）─────────────────────
  if (!config.__canonicalPath) {
    // 规范化：去掉开头的斜杠
    config.__canonicalPath = (config.url || '').replace(/^\//, '');
  }

  // ── 选择域名（仅首次请求；重试时已由 executeWithBudget 写入 baseURL）──
  if (!config.baseURL) {
    config.baseURL = domainConfig.getBestDomain();
  }

  // ── 只读接口判定（基于规范路径，而非拼接后的完整 URL）────────────────
  if (config.__isReadonly === undefined) {
    config.__isReadonly = domainConfig.isReadOnlyApi(config.__canonicalPath);
  }

  // ── Token ───────────────────────────────────────────────────────────────
  const storage = instance.localStorage || localStorage;
  const token = (storage && storage.getItem(TOKEN_KEY)) || queryString('token') || '';
  if (token) {
    config.headers.usertoken = token;
  }

  // ── DeviceID ────────────────────────────────────────────────────────────
  let deviceid = storage ? storage.getItem(DEVICE_ID_KEY) : '';
  try {
    if (/^\{.*\}$/.test(deviceid)) {
      const parsed = JSON.parse(deviceid);
      deviceid = isH5 ? parsed.DeviceID : parsed.myDeviceID;
    }
  } catch (e) {
    console.log('[request] 处理DeviceID出错', e);
    deviceid = '';
  }
  if (!deviceid) {
    deviceid = DEVICE_ID_DEFAULT;
    if (storage) {
      storage.setItem(DEVICE_ID_KEY, JSON.stringify({
        [isH5 ? 'DeviceID' : 'myDeviceID']: deviceid
      }));
    }
  }
  config.headers.deviceid = deviceid;

  // ── 公共头 ──────────────────────────────────────────────────────────────
  config.headers.source = config.headers.source || DEFAULT_SOURCE;
  config.headers.lytime = moment().format('yyyy-MM-DD HH:mm:ss');
  config.headers.versions = '1.0';
  config.headers.product = window.__my_res_ver;

  // ── 参数处理 ─────────────────────────────────────────────────────────────
  const type = config.method?.toLowerCase();
  const arrayFormat = config.headers.arrayFormat || 'indices';
  if (type === 'post' && config.headers['Content-Type'] === 'application/x-www-form-urlencoded; charset=utf-8') {
    config.data = qs.stringify(config.data, { allowDots: true, arrayFormat, lang: getApiLang(instance) });
  } else if (type === 'get' && config.params) {
    config.params = qs.parse(qs.stringify(config.params, { allowDots: true, arrayFormat, lang: getApiLang(instance) }));
  } else {
    config.data && Object.assign(config.data, { lang:getApiLang(instance) });
  }

  // ── Phase F: URL 拼接（始终基于 __canonicalPath，避免重复拼接）─────────
  config._url = config.__canonicalPath;
  if (config.serv === 'custom') {
    config._host = '';
  } else {
    config._host = config.baseURL + 'api/';
  }
  config.url = config._host + config._url;

  // ── notips 向后兼容：内部统一为 silent ──────────────────────────────────
  config.silent = config.silent ?? config.notips ?? false;

  recordApiRequestCount(config.method?.toUpperCase(), window.__my_res_ver, config.url);
  return config;
}, error => {
  return Promise.reject(error);
});

// ─────────────────────────────────────────────────────────────────────────────
// 响应拦截器
// ─────────────────────────────────────────────────────────────────────────────
instance.interceptors.response.use(
  response => {
    const requestETime = performance.now();
    const responseTime = requestETime - response.config.requestSTime;

    const isHighFreq = response.config.url.includes('/live/heartbeat');
    recordApiRequestDuration(responseTime, response.config.method?.toUpperCase(), window.__my_res_ver, response.config.url, response.status, isHighFreq);

    const tranDuration = parseFloat(response.headers.get('x-server-duration')) || 0;
    if (tranDuration > 0) {
      recordApiTranDuration(responseTime - tranDuration, response.config.method?.toUpperCase(), window.__my_res_ver, response.config.url, response.status, isHighFreq);
    }

    // 记录域名成功（EWMA RTT 更新）
    domainConfig.recordSuccess(response.config.baseURL, responseTime);

    // 处理用户信息更新（H5特有）
    if (isH5) {
      const getuserapi = ["/UserLogin", "/UserInfo"].find(api =>
        new RegExp(`${api}$`).test(response.config.url)
      );
      if (getuserapi && response.data?.data) {
        if (response.data.data.accessToken) {
          instance.mitter?.emit('accessTokenChange', response.data.data.accessToken);
          localStorage.setItem(TOKEN_KEY, response.data.data.accessToken);
        }
        if (response.data.data.id) {
          instance.mitter?.emit('userLogined', response.data.data);
        }
      }
    }

    // 业务层错误处理（HTTP 200 但业务码非正常）
    if (response.data && ((!response.data.success && response.data.message) || (response.data.code !== 200 && response.data.msg))) {
      const message = response.data.message || response.data.msg || '服务器开小差了哦~';

      // Phase E: 通过事件总线发出，不直接调用 UI 框架
      // _emitRequestErrorMessage(message, response.data.code, response.config);
      _emitRequestError({ message: message, response: { status: response.data.code } }, response.config);

      // 未登录处理
      if (message === "用户未登录" && instance.router) {
        const currentPath = instance.router.currentRoute.value.path;
        if (currentPath !== LOGIN_PATH) {
          instance.mitter.emit('triggerLogin', true);
        }
      }

      recordApiRequestErrorCount(response.config?.method, window.__my_res_ver, response.config?.url, response.data?.code);
    }

    return response;
  },

  // ── HTTP 层错误（4xx/5xx/超时/网络错误）─────────────────────────────────
  // 注意：executeWithBudget 在外层捕获后才会进入此处。
  // 对于由 executeWithBudget 发起的内层 instance(attemptConfig) 调用，
  // 错误会直接 throw 给 executeWithBudget 处理，不再显示 Toast，
  // 只有最终从 executeWithBudget 上抛的错误，才由最外层业务调用捕获后经此路径提示。
  async error => {
    if (error.config) {
      const requestETime = Date.now();
      const responseTime = requestETime - (error.config?.requestSTime || requestETime);

      let customHttpCode = ERROR_TYPE.UNKNOWN_ERR;
      if (error.code === 'ECONNABORTED' && (error.message || '').includes('timeout')) {
        customHttpCode = ERROR_TYPE.TIMEOUT;
      } else if (!error.response && (error.message || '').includes('Network Error')) {
        customHttpCode = ERROR_TYPE.NETWORK_ERROR;
      } else if (error.response?.status) {
        customHttpCode = error.response.status;
      }

      recordApiRequestErrorCount(error.config?.method, window.__my_res_ver, error.config?.url, customHttpCode);
      recordApiRequestDuration(responseTime, error.config?.method, window.__my_res_ver, error.config?.url, customHttpCode, false);

      const tranDuration = parseFloat(error.response?.headers?.get?.('x-server-duration')) || 0;
      if (tranDuration > 0) {
        recordApiTranDuration(responseTime - tranDuration, error.config.method?.toUpperCase(), window.__my_res_ver, error.config.url, customHttpCode, false);
      }

      // 记录域名失败（HTTP层失败统一记录，再交给上层策略处理）
      if (error.config.baseURL) {
        domainConfig.recordFail(error.config.baseURL);
      }
    }

    return Promise.reject(error);
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Phase E: 外部调用代理包装器
//
// 关键：axios.create() 返回的 instance 是一个绑定函数
// (Axios.prototype.request.bind(context))，直接覆盖 instance.request
// 只能拦截 instance.request(config) 调用，无法拦截 instance(config) 调用。
// 而外部使用 proxy.request(config) 实际上触发的是 instance(config) —— 即函数调用。
//
// 因此使用 ES6 Proxy 的 apply trap 拦截函数调用，确保所有调用路径
// 都经过 proxyRequest → executeWithBudget。
//
// 内部重试：executeWithBudget 中 instance(attemptConfig) 直接走原始 instance
// (模块作用域变量)，不经过 Proxy，避免递归。
// ─────────────────────────────────────────────────────────────────────────────
const originalRequest = instance.request.bind(instance);

async function proxyRequest(config) {
  // 内部重试调用（已在 executeWithBudget 内）直接透传
  if (config.__isRetrying) {
    return originalRequest(config);
  }

  // 锁定域名时不经过预算执行器，直接走原始请求（保持调试行为一致）
  if (domainConfig.isLocked) {
    try {
      return await originalRequest(config);
    } catch (err) {
      _emitRequestError(err, config);
      return Promise.reject(err);
    }
  }

  try {
    return await executeWithBudget(config);
  } catch (err) {
    _emitRequestError(err, config);
    return Promise.reject(err);
  }
}

// 同时覆盖 .request() 属性，确保两种调用方式都被拦截
instance.request = proxyRequest;

function _emitRequestError(err, config) {
  const silent = config?.silent ?? config?.notips ?? false;
  if (silent) return;

  let message = '';
  const status = err.response?.status;
  if (status === 429) {
    message = '请求过于频繁，请稍后再试';
  } else if (status === 503) {
    message = '服务繁忙，请稍后再试';
  } else if (status === 502) {
    message = '网关错误，请稍后再试';
  } else if (status === 403) {
    message = '您所在的地区或IP无法访问本服务';
    instance.mitter?.emit('http403', err.response);
  } else if (err.code === 'ECONNABORTED' || (err.message || '').toLowerCase().includes('timeout')) {
    message = '请求超时，请检查网络';
  } else if (!err.response && (err.message || '').includes('Network Error')) {
    message = '网络连接失败';
  } else if (err.response?.data?.message) {
    message = err.response.data.message;
  } else if ((err.message || '').includes('所有域名') || (err.message || '').includes('预算')) {
    message = '网络不稳定，请稍后重试';
  } else {
    message = err.message;
  }

  if (message) {
    instance.mitter?.emit('request:error', { message, code: status || err.code, config });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase G: 调试与自测接口
// ─────────────────────────────────────────────────────────────────────────────
instance.domain = {
  lock: (domain) => domainConfig.lockDomain(domain),
  unlock: () => domainConfig.unlockDomain(),
  getCurrent: () => domainConfig.isLocked
    ? domainConfig.completeProtocol(domainConfig.lockedDomain)
    : domainConfig.getBestDomain(),
  getStats: () => JSON.parse(JSON.stringify(domainConfig.domainStats)),
  updateList: (newDomains) => {
    const ok = domainConfig.updateDomainList(newDomains);
    if (ok) pingScheduler.start(); // 域名列表就绪后启动 ping 探测
    return ok;
  },
  updateReadOnlyApis: (apis) => domainConfig.updateReadOnlyApis(apis),

  // 健康报告：输出每个域名的评分与熔断状态
  getHealthReport() {
    return domainConfig.domainList.map(domain => {
      const s = domainConfig.domainStats[domain] || {};
      return {
        domain,
        score: domainConfig.calculateScore(domain),
        circuitOpen: domainConfig.isCircuitOpen(domain),
        circuitUntil: s.circuitUntil ? new Date(s.circuitUntil).toISOString() : null,
        failCount: s.failCount,
        lastFailTime: s.lastFailTime ? new Date(s.lastFailTime).toISOString() : null,
        ewmaRtt: s.ewmaRtt,
        pingRtt: s.pingRtt,
        pingCost: s.pingCost
      };
    });
  },

  // 调试模拟：将浏览器 Block 映射为指定错误类型，便于观察各策略行为
  // type: '429' | '502' | '503' | 'timeout' | null(关闭)
  // 用法：request.domain.simulate('502')  → 之后浏览器 Block 的请求会走 502 重试策略
  //       request.domain.simulate(null)   → 关闭模拟
  simulate(type) {
    const valid = [null, '429', '502', '503', 'timeout'];
    const t = type === null || type === false ? null : String(type);
    if (t !== null && !valid.includes(t)) {
      console.error(`[request] simulate: 无效类型 "${type}", 可选: ${valid.filter(Boolean).join(', ')}`);
      return;
    }
    domainConfig._debugSimulation = t;
    console.log(`[request] 调试模拟已${t ? '开启: 网络错误 → ' + t : '关闭'}`);
  },

  // 重置所有域名统计（调试用）
  resetStats() {
    domainConfig.domainList.forEach(domain => {
      domainConfig.domainStats[domain] = domainConfig._defaultStats();
    });
    domainConfig._saveDomainStats();
    console.log('[request] 域名统计已重置');
  },

  // ── 内建自测方法 ──────────────────────────────────────────────────────────
  // 用法：await request.domain.runSelfTest()
  // 返回：{ passed, failed, results: [...] }
  async runSelfTest() {
    const results = [];
    const log = (name, pass, detail) => {
      results.push({ name, pass, detail });
      console[pass ? 'log' : 'error'](`[SelfTest] ${pass ? '✓' : '✗'} ${name}`, detail || '');
    };

    // ── 测试1: 域名评分基础逻辑 ──────────────────────────────────────────
    (() => {
      const backup = JSON.parse(JSON.stringify(domainConfig.domainStats));
      const testDomains = ['test-a.example.com', 'test-b.example.com', 'test-c.example.com'];
      domainConfig.domainList = testDomains;
      testDomains.forEach(d => { domainConfig.domainStats[d] = domainConfig._defaultStats(); });

      // A 域名：多次失败并熔断
      domainConfig.recordFail('test-a.example.com');
      domainConfig.recordFail('test-a.example.com');
      const scoreA = domainConfig.calculateScore('test-a.example.com');

      // B 域名：无记录
      const scoreB = domainConfig.calculateScore('test-b.example.com');

      // C 域名：快速响应记录
      domainConfig.recordSuccess('test-c.example.com', 80);
      const scoreC = domainConfig.calculateScore('test-c.example.com');

      const pass = scoreC > scoreB && scoreB > scoreA;
      log('域名评分顺序: C > B > A（熔断 < 无记录 < 快速）', pass, { scoreA, scoreB, scoreC });

      // 恢复
      domainConfig.domainStats = Object.assign({ _lastSaved: 0 }, backup);
    })();

    // ── 测试2: getBestDomain 不回跳已尝试域名 ────────────────────────────
    (() => {
      const backup = JSON.parse(JSON.stringify(domainConfig.domainStats));
      const testDomains = ['d1.example.com', 'd2.example.com', 'd3.example.com'];
      const origList = domainConfig.domainList;
      domainConfig.domainList = testDomains;
      testDomains.forEach(d => { domainConfig.domainStats[d] = domainConfig._defaultStats(); });

      // d1 失败并熔断
      domainConfig.recordFail('d1.example.com');
      domainConfig.recordFail('d1.example.com');

      const best = domainConfig.getBestDomain(['d1.example.com']);
      const pass = best && !best.includes('d1.example.com');
      log('excludeDomains 防回跳: getBestDomain 排除 d1', pass, { best });

      domainConfig.domainList = origList;
      domainConfig.domainStats = Object.assign({ _lastSaved: 0 }, backup);
    })();

    // ── 测试3: silent/notips 兼容 ─────────────────────────────────────────
    (() => {
      let emitted = false;
      const origMitter = instance.mitter;
      instance.mitter = { emit: (evt) => { if (evt === 'request:error') emitted = true; } };

      // notips=true 不应 emit
      _emitRequestError({ message: 'test', response: { status: 500 } }, { notips: true });
      const passNotips = !emitted;

      // silent=true 不应 emit
      emitted = false;
      _emitRequestError({ message: 'test', response: { status: 500 } }, { silent: true });
      const passSilent = !emitted;

      // 无标志应 emit
      emitted = false;
      _emitRequestError({ message: 'test', response: { status: 500, data: { message: 'err' } } }, {});
      const passEmit = emitted;

      instance.mitter = origMitter;
      const pass = passNotips && passSilent; // passEmit 依赖 mitter 实际存在，仅在有 mitter 时有意义
      log('silent/notips 抑制 request:error 事件', pass, { passNotips, passSilent, passEmit });
    })();

    // ── 测试4: 429 策略（模拟）────────────────────────────────────────────
    (() => {
      // 验证 _emitRequestError 对 429 输出正确消息
      let emittedMsg = null;
      const origMitter = instance.mitter;
      instance.mitter = { emit: (_, payload) => { emittedMsg = payload?.message; } };
      _emitRequestError({ response: { status: 429 } }, {});
      instance.mitter = origMitter;
      const pass = typeof emittedMsg === 'string' && emittedMsg.length > 0;
      log('429 对外报错消息非空', pass, { emittedMsg });
    })();

    // ── 测试5: /ping 评分融合 ─────────────────────────────────────────────
    (() => {
      const backup = JSON.parse(JSON.stringify(domainConfig.domainStats));
      const origList = domainConfig.domainList;
      const testDomains = ['p1.example.com', 'p2.example.com'];
      domainConfig.domainList = testDomains;
      testDomains.forEach(d => { domainConfig.domainStats[d] = domainConfig._defaultStats(); });

      // p1 ping 快且成本低
      domainConfig.recordPing('p1.example.com', 50, 1);
      // p2 ping 慢且成本高
      domainConfig.recordPing('p2.example.com', 4000, 90);

      const s1 = domainConfig.calculateScore('p1.example.com');
      const s2 = domainConfig.calculateScore('p2.example.com');
      const pass = s1 > s2;
      log('/ping 评分融合: 低RTT低成本 > 高RTT高成本', pass, { s1, s2 });

      domainConfig.domainList = origList;
      domainConfig.domainStats = Object.assign({ _lastSaved: 0 }, backup);
    })();

    // ── 测试6: 全熔断降级（不返回空字符串）────────────────────────────────
    (() => {
      const backup = JSON.parse(JSON.stringify(domainConfig.domainStats));
      const origList = domainConfig.domainList;
      const testDomains = ['fb1.example.com', 'fb2.example.com'];
      domainConfig.domainList = testDomains;
      testDomains.forEach(d => {
        domainConfig.domainStats[d] = domainConfig._defaultStats();
        domainConfig.domainStats[d].circuitUntil = Date.now() + 999999;
      });

      const best = domainConfig.getBestDomain([]);
      const pass = best.length > 0; // 全熔断时应降级返回最不差域名
      log('全熔断降级: getBestDomain 不返回空字符串', pass, { best });

      domainConfig.domainList = origList;
      domainConfig.domainStats = Object.assign({ _lastSaved: 0 }, backup);
    })();

    const passed = results.filter(r => r.pass).length;
    const failed = results.filter(r => !r.pass).length;
    console.log(`[SelfTest] 完成: ${passed} 通过, ${failed} 失败`);
    return { passed, failed, results };
  }
};

// 启动 ping 探测（在 updateList 后自动触发，这里兜底：如果列表已有数据则立即启动）
if (domainConfig.domainList.length > 0) {
  pingScheduler.start();
}

// ─────────────────────────────────────────────────────────────────────────────
// 导出：通过 Proxy apply trap 拦截 instance(config) 函数调用
// 外部得到的 requestProxy 在属性访问上与 instance 完全一致（mitter, domain, ...），
// 但函数调用会路由到 proxyRequest → executeWithBudget。
// 内部 executeWithBudget 中的 instance(attemptConfig) 仍走原始路径，不触发 Proxy。
// ─────────────────────────────────────────────────────────────────────────────
const requestProxy = new Proxy(instance, {
  apply(_target, _thisArg, args) {
    return proxyRequest(args[0]);
  }
});

export default requestProxy;
